/* eslint-disable react/destructuring-assignment */
import { FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import {
  composeValidators,
  minLength,
  required,
  userNameValid,
} from 'utils/validators';
import FieldLocation from 'components/common/FieldLocation';

import Input from 'components/common/Input';
import { MailIcon, PhoneIcon, WorldIcon } from 'public/assets/icons';
import FieldSwitchTab from 'components/common/FieldSwitchTab';
import { IEditProfileForm } from './IEditProfileForm';

import {
  CoverImageUploader,
  EmailInput,
  FinalFormWrapper,
  FormWrapper,
  ImageUploaderWrapper,
  InputWrapper,
  PhoneInput,
  ProfileImageUploader,
  SectionHeader,
  SectionWrapper,
  SelectorWrapper,
  WebsiteInput,
} from './EditProfileForm.styles';
import Interests from './Interests';
import MyBrands from './MyBrands';
import MyCollaborators from './MyCollaborators';

const EditProfileForm = (props: IEditProfileForm) => {
  const { t } = useTranslation();

  return (
    <FinalFormWrapper
      {...props}
      render={(formRenderProps: FormRenderProps) => {
        const { handleSubmit, values, form } = formRenderProps;
        const { inProgress, ready, onIconClick } = props;
        return (
          <FormWrapper
            onSubmit={handleSubmit}
            onIconClick={onIconClick}
            isHeaderBorder
            headerText={t('editUserProfile:editProfile')}
            submitText={t('editUserProfile:save')}
          >
            <ImageUploaderWrapper>
              <CoverImageUploader
                name="coverPhoto"
                isOnePhoto
                label="Add a photo"
              />
              <ProfileImageUploader
                name="profilePhoto"
                isProfileImages
                isOnePhoto
              />
            </ImageUploaderWrapper>

            <SectionWrapper pt={40}>
              <SectionHeader type="h3">
                {t('editUserProfile:profileType')}
              </SectionHeader>
              <FieldSwitchTab
                id="isBusiness"
                name="isBusiness"
                defaultValue={false}
                labelLeft={t('buttonGroupLabels:individual')}
                labelRight={t('buttonGroupLabels:business')}
              />
            </SectionWrapper>

            <SectionWrapper>
              <Input
                id="username"
                name="username"
                type="text"
                label={t('editUserProfile:myUsername')}
                validate={composeValidators(
                  required(t('error:thisFieldIsRequired')),
                  userNameValid(t('error:vaildUsername')),
                  minLength(t('error:atLeastThree'), 3)
                )}
              />
              <SelectorWrapper>
                <Interests />
                <MyBrands />
              </SelectorWrapper>
            </SectionWrapper>
            <SectionWrapper>
              <SectionHeader type="h3">
                {t('editUserProfile:myInformation')}
              </SectionHeader>
              <InputWrapper>
                <Input
                  id="fullName"
                  name="fullName"
                  type="text"
                  label={t('editUserProfile:completeName')}
                />
              </InputWrapper>
              <InputWrapper>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  label={t('editUserProfile:bio')}
                  placeholder={t('editUserProfile:addBio')}
                  maxLength={500}
                />
              </InputWrapper>
              <FieldLocation
                name="location"
                label={t('addNewGroup:cityOrLocation')}
                headerText={t('map:tagLocation')}
                validate={composeValidators(
                  required(t('error:thisFieldIsRequired'))
                )}
              />
              {values?.isBusiness && (
                <>
                  <InputWrapper>
                    <PhoneInput
                      id="phone"
                      name="phone"
                      type="text"
                      prefix={<PhoneIcon />}
                      label={t('editUserProfile:businessPhone')}
                      placeholder={t('editUserProfile:phone')}
                    />
                  </InputWrapper>
                  <InputWrapper>
                    <EmailInput
                      id="email"
                      name="email"
                      type="text"
                      prefix={<MailIcon />}
                      label={t('editUserProfile:businessEmail')}
                      placeholder={t('editUserProfile:email')}
                    />
                  </InputWrapper>
                </>
              )}
              <InputWrapper>
                <WebsiteInput
                  id="website"
                  name="website"
                  type="text"
                  prefix={<WorldIcon />}
                  label={t('editUserProfile:myWebsite')}
                  placeholder={t('editUserProfile:website')}
                />
              </InputWrapper>
              <SelectorWrapper>
                <MyCollaborators />
              </SelectorWrapper>
            </SectionWrapper>
          </FormWrapper>
        );
      }}
    />
  );
};

export default EditProfileForm;
